import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CrmActor} from '../../domain/entities/Pipeline.ts'
import {ColumnSource, useBoardColumnMore} from '../../hooks/usePipelines.ts'
import {AutoIcon, Spinner} from './PipelineParts.tsx'
import {ColumnDragProps} from './useBoardDrag.ts'

type ColumnData<TCard> = {
    stage: string
    actor: CrmActor
    count: number
    cards: TCard[]
    nextCursor: string | null
}

type BoardColumnProps<TCard> = ColumnDragProps & {
    column: ColumnData<TCard>
    // Where "Load more" reads the next pages of this column from.
    source: ColumnSource
    // Green header (Registered, Delivered).
    success?: boolean
    // Shown while a card hovers this column as a valid drop target.
    dropHint?: string | null
    collapsed: boolean
    onToggleCollapsed: () => void
    renderCard: (card: TCard) => React.ReactNode
}

export const ActorMark: React.FC<{actor: string | null}> = ({actor}) => {
    const {t} = useTranslation('dashboard')
    if (!actor) return null
    const label = t(`pipelines.actor.${actor}`, {defaultValue: actor})
    if (actor === 'system') return <span title={label} className="flex-none"><span className="sr-only">{label}</span><AutoIcon className="w-4 h-4"/></span>
    if (actor === 'customer') {
        return (
            <span title={label} className="flex-none text-neutralBlack-600">
                <span className="sr-only">{label}</span>
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
            </span>
        )
    }
    if (actor === 'third_party') {
        return (
            <span title={label} className="flex-none text-neutralBlack-600">
                <span className="sr-only">{label}</span>
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>
            </span>
        )
    }
    if (actor === 'person') {
        return (
            <span title={label} className="flex-none text-[#14343B]">
                <span className="sr-only">{label}</span>
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            </span>
        )
    }
    return null
}

// One column of either board: header with its actor mark, collapsible, its
// cards and "Load more". The card itself comes from `renderCard`.
export function BoardColumn<TCard extends {id: string}>({
    column, source, success = false, dropHint, collapsed, onToggleCollapsed, renderCard,
    onColumnDragEnter, onColumnDragOver, onColumnDragLeave, onColumnDrop, isDropTarget,
}: BoardColumnProps<TCard>) {
    const {t} = useTranslation('dashboard')
    const [moreRequested, setMoreRequested] = useState(false)
    const {moreCards, hasMore, isLoadingMore, loadMore} = useBoardColumnMore<TCard>(source, column.nextCursor, moreRequested)
    const name = t(`pipelines.stage.${column.stage}`)
    const isRegistered = success

    const seen = new Set<string>()
    const cards = [...column.cards, ...(moreRequested ? moreCards : [])].filter((card) => {
        if (seen.has(card.id)) return false
        seen.add(card.id)
        return true
    })

    const countClass = `min-w-[22px] h-5 px-1.5 rounded-full text-[11.5px] font-semibold inline-flex items-center justify-center flex-none ${
        isRegistered ? 'bg-[#DDF0E8] text-[#1F6B4F]' : 'bg-neutralBlack-200 text-neutralBlack-700'}`
    const shell = `flex-none flex flex-col min-h-0 rounded-[14px] border transition-colors duration-200 ease-out ${
        isDropTarget ? 'border-[#14343B] bg-[#ECF2F2]'
            : isRegistered ? 'border-[#CFE8DA] bg-[#F1F9F4]' : 'border-neutralBlack-200 bg-neutralBlack-50'}`
    const dragProps = {
        onDragEnter: onColumnDragEnter,
        onDragOver: onColumnDragOver,
        onDragLeave: onColumnDragLeave,
        onDrop: onColumnDrop,
    }

    if (collapsed) {
        return (
            <section aria-label={name} className={`${shell} w-[52px]`} {...dragProps}>
                <button type="button" onClick={onToggleCollapsed} aria-expanded={false}
                        aria-label={t('pipelines.column.expand', {name})}
                        className="flex-1 flex flex-col items-center gap-3.5 py-3.5 rounded-[14px] hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                    <span className={countClass}>{column.count}</span>
                    <span className={`[writing-mode:vertical-rl] text-[12.5px] font-semibold whitespace-nowrap ${isRegistered ? 'text-[#1F6B4F]' : 'text-neutralBlack-700'}`}>{name}</span>
                </button>
            </section>
        )
    }

    return (
        <section aria-label={t('pipelines.column.label', {name, count: column.count})} className={`${shell} w-[300px]`} {...dragProps}>
            <header className="flex items-center gap-2 pl-2 pr-3 pt-3 pb-2.5 flex-none">
                <button type="button" onClick={onToggleCollapsed} aria-expanded={true}
                        aria-label={t('pipelines.column.collapse', {name})}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-neutralBlack-600 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m11 17-5-5 5-5"/><path d="m18 17-5-5 5-5"/></svg>
                </button>
                {isRegistered && <span aria-hidden="true" className="w-2 h-2 rounded-full bg-[#1F7A4D] flex-none"/>}
                <h2 className="text-[13px] font-semibold text-neutralBlack-900 truncate min-w-0">{name}</h2>
                <ActorMark actor={column.actor}/>
                <span className="flex-1"/>
                <span className={countClass}>{column.count}</span>
            </header>
            {isDropTarget && dropHint && (
                <p className="px-3 pb-2 text-[12px] font-medium text-[#14343B]">{dropHint}</p>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto px-2.5 pb-3 flex flex-col gap-2.5">
                {cards.map((card) => <React.Fragment key={card.id}>{renderCard(card)}</React.Fragment>)}
                {cards.length === 0 && (
                    <p className="py-5 px-2 text-center text-[12px] text-neutralBlack-600">{t('pipelines.column.empty')}</p>
                )}
                {hasMore && (
                    <button type="button" disabled={isLoadingMore}
                            onClick={() => (moreRequested ? loadMore() : setMoreRequested(true))}
                            className="h-9 rounded-lg border border-neutralBlack-200 bg-white text-[12.5px] font-medium text-neutralBlack-800 inline-flex items-center justify-center gap-2 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out disabled:cursor-wait">
                        {isLoadingMore ? <Spinner/> : t('pipelines.column.loadMore', {count: Math.max(0, column.count - cards.length)})}
                    </button>
                )}
            </div>
        </section>
    )
}
